import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Plus, Pencil, Trash2, ExternalLink } from 'lucide-react';
import { AdminLayout } from '../../components/admin';
import { Button } from '../../components/ui';
import { toast } from '../../components/ui/Toaster';
import { blogService, BlogPost } from '../../services/blogService';
import { getPublicBlogUrl } from '../../utils/blogUrls';

const formatDate = (value?: string | null) => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const BlogManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      setLoading(true);
      const data = await blogService.listAdmin();
      setPosts(data);
    } catch (error) {
      console.error('Error loading blog posts:', error);
      toast.error('Fout', 'Kon blogberichten niet laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleDelete = async (post: BlogPost) => {
    if (!window.confirm(`Weet je zeker dat je "${post.title}" wilt verwijderen?`)) return;
    try {
      await blogService.remove(post.id);
      toast.success('Verwijderd', 'Blogbericht verwijderd');
      setPosts((prev) => prev.filter((p) => p.id !== post.id));
    } catch (error: any) {
      console.error('Error deleting blog post:', error);
      toast.error('Fout', error.message || 'Kon blogbericht niet verwijderen');
    }
  };

  return (
    <AdminLayout title="Blog" subtitle="Beheer je blogberichten">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center text-fg-muted text-sm">
            <FileText className="h-4 w-4 mr-2 text-neon-cyan" />
            {posts.length} {posts.length === 1 ? 'bericht' : 'berichten'}
          </div>
          <Button onClick={() => navigate('/admin/blog/new')} className="flex items-center">
            <Plus className="h-4 w-4 mr-2" />
            Nieuw bericht
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="text-fg-muted">Laden...</div>
          </div>
        ) : posts.length === 0 ? (
          <div className="sf-card p-10 text-center text-fg-muted">
            Nog geen blogberichten. Maak je eerste bericht aan.
          </div>
        ) : (
          <div className="sf-card divide-y divide-white/[.06]">
            {posts.map((post) => (
              <div
                key={post.id}
                className="flex flex-col sm:flex-row sm:items-center gap-3 p-4"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-fg-bright truncate">{post.title}</h3>
                    {post.isPublished ? (
                      <span className="sf-badge-lime">Gepubliceerd</span>
                    ) : (
                      <span className="sf-badge-cyan">Concept</span>
                    )}
                  </div>
                  <p className="text-xs text-fg-muted mt-1 font-mono truncate">
                    /blog/{post.slug} · {formatDate(post.publishedAt || post.createdAt)}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {post.isPublished && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(getPublicBlogUrl(post.slug), '_blank')}
                      className="flex items-center"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/admin/blog/${post.id}`)}
                    className="flex items-center"
                  >
                    <Pencil className="h-4 w-4 mr-1" />
                    Bewerk
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(post)}
                    className="flex items-center text-neon-pink-soft"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default BlogManagementPage;
